
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const ExportContactSection = () => {
  const { t } = useTranslation('home');

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    country: '',
    productCategory: '',
    exportVolume: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6fn4qan5jm489bu5nh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          phone: '',
          email: '',
          country: '',
          productCategory: '',
          exportVolume: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const productCategories = [
    { key: 'contact.categories.food', value: t('contact.categories.food') },
    { key: 'contact.categories.spices', value: t('contact.categories.spices') },
    { key: 'contact.categories.noodles', value: t('contact.categories.noodles') },
    { key: 'contact.categories.snacks', value: t('contact.categories.snacks') },
    { key: 'contact.categories.consumer', value: t('contact.categories.consumer') },
    { key: 'contact.categories.cosmetics', value: t('contact.categories.cosmetics') },
    { key: 'contact.categories.household', value: t('contact.categories.household') },
    { key: 'contact.categories.other', value: t('contact.categories.other') },
  ];

  const exportVolumes = [
    { key: 'contact.volumes.under1', value: t('contact.volumes.under1') },
    { key: 'contact.volumes.1to3', value: t('contact.volumes.1to3') },
    { key: 'contact.volumes.3to5', value: t('contact.volumes.3to5') },
    { key: 'contact.volumes.over5', value: t('contact.volumes.over5') },
  ];

  const countries = [
    { key: 'contact.countries.vietnam', value: t('contact.countries.vietnam') },
    { key: 'contact.countries.china', value: t('contact.countries.china') },
    { key: 'contact.countries.thailand', value: t('contact.countries.thailand') },
    { key: 'contact.countries.japan', value: t('contact.countries.japan') },
    { key: 'contact.countries.korea', value: t('contact.countries.korea') },
    { key: 'contact.countries.indonesia', value: t('contact.countries.indonesia') },
    { key: 'contact.countries.malaysia', value: t('contact.countries.malaysia') },
    { key: 'contact.countries.philippines', value: t('contact.countries.philippines') },
    { key: 'contact.countries.india', value: t('contact.countries.india') },
    { key: 'contact.countries.other', value: t('contact.countries.other') },
  ];

  const contactInfoItems = [
    {
      icon: 'ri-phone-line',
      label: t('contact.hotlineLabel'),
      value: '+371 2572 6969',
      sub: t('contact.hotlineSub'),
    },
    {
      icon: 'ri-mail-line',
      label: t('contact.form.email'),
      value: 'contact@thedaeg.com',
      sub: t('contact.emailSub'),
    },
    {
      icon: 'ri-map-pin-line',
      label: t('contact.officeLabel'),
      value: 'Krisjana Barona Iela 96/98',
      sub: t('contact.officeSub'),
    },
  ];

  return (
    <section
      id="contact-export"
      className="py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-b from-gray-950 to-black relative overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent"></div>
      <div className="absolute top-20 right-10 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none hidden sm:block"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-yellow-600/5 rounded-full blur-3xl pointer-events-none hidden sm:block"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12 md:gap-16 items-start">

          {/* Left info */}
          <div className="lg:col-span-2 text-white">
            <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 bg-yellow-500/15 border border-yellow-400/30 rounded-full text-xs text-yellow-300 font-semibold uppercase tracking-wider mb-4 sm:mb-5">
              {t('contact.badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-5 font-serif leading-tight">
              {t('contact.title')}
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 sm:mb-10">
              {t('contact.description')}
            </p>

            {/* Contact info */}
            <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-10">
              {contactInfoItems.map((c, i) => (
                <div key={i} className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-yellow-500/15 rounded-xl flex-shrink-0">
                    <i className={`${c.icon} text-lg sm:text-xl text-yellow-400`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{c.label}</p>
                    <p className="text-white font-semibold text-sm">{c.value}</p>
                    <p className="text-gray-500 text-xs">{c.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Commitment box */}
            <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-400/20 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <i className="ri-double-quotes-l text-2xl sm:text-3xl text-yellow-400"></i>
                <span className="text-yellow-300 font-semibold text-xs sm:text-sm">{t('contact.commitmentTitle')}</span>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed italic">
                &ldquo;{t('contact.commitmentQuote')}&rdquo;
              </p>
              <div className="mt-3 sm:mt-4 flex items-center gap-2 sm:gap-3">
                <img
                  src="https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/6e9247accedb8e65ccee3efa8f0eef11.jpeg"
                  alt="The Daeg International"
                  className="h-7 sm:h-8 w-auto object-contain"
                />
                <div>
                  <p className="text-white text-xs font-bold">The Daeg International Trade</p>
                  <p className="text-gray-500 text-xs">{t('contact.companyEstablished')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-3 bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm rounded-2xl p-4 sm:p-7 md:p-10 border border-yellow-500/20 shadow-2xl shadow-yellow-500/5">
            <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2 font-serif">
              {t('contact.formTitle')}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-8">{t('contact.formSubtitle')}</p>

            <form
              id="export-partner-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-3 sm:space-y-5"
            >
              {/* Company Name */}
              <div>
                <label htmlFor="expCompanyName" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.company')} <span className="text-yellow-400">*</span>
                </label>
                <input
                  type="text"
                  id="expCompanyName"
                  name="companyName"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>

              {/* Contact Person */}
              <div>
                <label htmlFor="expContactPerson" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.name')} <span className="text-yellow-400">*</span>
                </label>
                <input
                  type="text"
                  id="expContactPerson"
                  name="contactPerson"
                  required
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                  placeholder={t('contact.form.namePlaceholder')}
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="expPhone" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.phone')} <span className="text-yellow-400">*</span>
                </label>
                <input
                  type="tel"
                  id="expPhone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                  placeholder={t('contact.form.phonePlaceholder')}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="expEmail" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.email')} <span className="text-yellow-400">*</span>
                </label>
                <input
                  type="email"
                  id="expEmail"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm placeholder-gray-600 transition-all"
                  placeholder={t('contact.form.emailPlaceholder')}
                />
              </div>

              {/* Country */}
              <div>
                <label htmlFor="expCountry" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.country')} <span className="text-yellow-400">*</span>
                </label>
                <select
                  id="expCountry"
                  name="country"
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm cursor-pointer transition-all"
                >
                  <option value="" className="bg-gray-900">{t('contact.form.countryPlaceholder')}</option>
                  {countries.map((c) => (
                    <option key={c.key} value={c.value} className="bg-gray-900">{c.value}</option>
                  ))}
                </select>
              </div>

              {/* Product Category */}
              <div>
                <label htmlFor="expProductCategory" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.product')} <span className="text-yellow-400">*</span>
                </label>
                <select
                  id="expProductCategory"
                  name="productCategory"
                  required
                  value={formData.productCategory}
                  onChange={(e) => setFormData({ ...formData, productCategory: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm cursor-pointer transition-all"
                >
                  <option value="" className="bg-gray-900">{t('contact.form.productPlaceholder')}</option>
                  {productCategories.map((cat) => (
                    <option key={cat.key} value={cat.value} className="bg-gray-900">{cat.value}</option>
                  ))}
                </select>
              </div>

              {/* Export Volume */}
              <div>
                <label htmlFor="expVolume" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.volume')}
                </label>
                <select
                  id="expVolume"
                  name="exportVolume"
                  value={formData.exportVolume}
                  onChange={(e) => setFormData({ ...formData, exportVolume: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm cursor-pointer transition-all"
                >
                  <option value="" className="bg-gray-900">{t('contact.form.volumePlaceholder')}</option>
                  {exportVolumes.map((v) => (
                    <option key={v.key} value={v.value} className="bg-gray-900">{v.value}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="expMessage" className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                  {t('contact.form.message')}
                </label>
                <textarea
                  id="expMessage"
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-900/80 border border-gray-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white text-sm placeholder-gray-600 resize-none transition-all"
                  placeholder={t('contact.form.messagePlaceholder')}
                ></textarea>
                <p className="text-xs text-gray-600 mt-1 text-right">{formData.message.length}/500</p>
              </div>

              {/* Status messages */}
              {submitStatus === 'success' && (
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <i className="ri-checkbox-circle-line text-green-400 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-green-300 text-xs sm:text-sm">{t('contact.success')}</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <i className="ri-error-warning-line text-red-400 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-red-300 text-xs sm:text-sm">{t('contact.error')}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full py-3 sm:py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black text-sm sm:text-base font-bold rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg shadow-yellow-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> {t('contact.form.submitting')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line"></i> {t('contact.form.submit')}
                  </span>
                )}
              </button>

              <p className="text-center text-xs text-gray-600 leading-relaxed">
                {t('contact.form.privacyNote')}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExportContactSection;
